import { useState, useCallback, useEffect } from 'react';
import { WelcomeBoardLayout, WelcomeBoardItem } from '../../../../common/interfaces';
import { WidgetConfig } from '../types';
import { parseGridPosition } from '../utils';

export const useLayoutState = (initialLayout?: WelcomeBoardLayout) => {
    // State management
    const [layout, setLayout] = useState<WelcomeBoardLayout>(
        initialLayout || {
            id: null,
            clientId: '',
            name: 'New Layout',
            gridColumnsTemplate: 'repeat(3, 1fr)',
            gridRowsTemplate: 'repeat(3, 1fr)',
            welcomeBoardItems: []
        }
    );

    // Update layout when initialLayout prop changes
    useEffect(() => {
        if (initialLayout) {
            setLayout(initialLayout);
        }
    }, [initialLayout]);
    
    const [selectedWidget, setSelectedWidget] = useState<WelcomeBoardItem | null>(null);
    const [originalSelectedWidget, setOriginalSelectedWidget] = useState<WelcomeBoardItem | null>(null);
    
    // User-friendly widget position state
    const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>({
        colStart: 1,
        colSpan: 1,
        rowStart: 1,
        rowSpan: 1
    });

    // Dialog states
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [configDialogOpen, setConfigDialogOpen] = useState(false);
    const [gridConfigOpen, setGridConfigOpen] = useState(false);
    const [exampleLayoutsOpen, setExampleLayoutsOpen] = useState(false);
    
    // Alert states
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    // Widget selection and configuration
    const handleWidgetClick = useCallback((item: WelcomeBoardItem) => {
        setSelectedWidget({ ...item }); // Create a copy for editing
        setOriginalSelectedWidget(item); // Keep reference to original
        
        // Parse current position into user-friendly values
        const colPos = parseGridPosition(item.gridColumn);
        const rowPos = parseGridPosition(item.gridRow);
        setWidgetConfig({
            colStart: colPos.start,
            colSpan: colPos.span,
            rowStart: rowPos.start,
            rowSpan: rowPos.span
        });
        
        setConfigDialogOpen(true);
    }, []);

    const handleWidgetDelete = useCallback((item: WelcomeBoardItem) => {
        setLayout(prev => ({
            ...prev,
            welcomeBoardItems: prev.welcomeBoardItems.filter(w => w !== item)
        }));
    }, []);

    const showAlertMessage = useCallback((message: string) => {
        setAlertMessage(message);
        setShowAlert(true);
    }, []);

    const handleClearLayout = useCallback(() => {
        setLayout(prev => ({
            ...prev,
            welcomeBoardItems: []
        }));
        showAlertMessage('Layout cleared successfully!');
    }, [showAlertMessage]);

    const closeConfigDialog = useCallback(() => {
        setConfigDialogOpen(false);
        setSelectedWidget(null);
        setOriginalSelectedWidget(null);
        setWidgetConfig({ colStart: 1, colSpan: 1, rowStart: 1, rowSpan: 1 });
    }, []);

    return {
        // State
        layout,
        setLayout,
        selectedWidget,
        setSelectedWidget,
        originalSelectedWidget,
        setOriginalSelectedWidget,
        widgetConfig,
        setWidgetConfig,
        drawerOpen,
        setDrawerOpen,
        configDialogOpen,
        setConfigDialogOpen,
        gridConfigOpen,
        setGridConfigOpen,
        exampleLayoutsOpen,
        setExampleLayoutsOpen,
        showAlert,
        setShowAlert,
        alertMessage,
        setAlertMessage,
        
        // Actions
        handleWidgetClick,
        handleWidgetDelete,
        showAlertMessage,
        handleClearLayout,
        closeConfigDialog
    };
};